<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { Loader2, TriangleAlert } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import type { FormContext } from 'vee-validate'
import { configure, useForm } from 'vee-validate'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import LoadingOverlay from 'vue-loading-overlay'
import * as z from 'zod'
import DialogCheck from '@/components/DialogCheck.vue'
import { generateUsername } from '@/composables/src/useUsername'
import { Alert, AlertDescription } from '@/foundation/alert'
import { AutoForm } from '@/foundation/auto-form'
import PhoneInput from '@/foundation/auto-form/AutoFormPhoneNumber.vue'
import { Button } from '@/foundation/button'
import { FormControl, FormField, FormItem, FormMessage } from '@/foundation/form'
import { Input } from '@/foundation/input'
import { Label } from '@/foundation/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/foundation/select'
import { setCompanyMember } from '@/service/companies'
import { createContact, updateContact } from '@/service/users'
import { useCompanyStore } from '@/stores/companies'
import { normalizePhoneNumber } from '@/utils/utils'
import { roles } from '@/views/tickets/data/data'

const props = defineProps<{
  companyId: string | null | undefined
  contact: any
  isParentSubmitting?: boolean
  overrideSubmitHandler?: (formData: Record<string, any>, contactId?: string) => Promise<void>
}>()

configure({
  bails: false,
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: false,
  validateOnModelUpdate: true,
})

const emit = defineEmits<{
  (e: 'dialogShouldClose'): void
  (e: 'closeDialog', { companyIdToBeRefetched, updateCount }: { companyIdToBeRefetched: string; updateCount: boolean }): void
}>()

const handleDialogShouldClose = () => {
  emit('dialogShouldClose')
}

const isValidEmailAddress = (value: string) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return emailRegex.test(value)
}

const isLoadingInternally = ref(false)
const isFormSubmittedOnce = ref(false)
const generalBackendError = ref<string | null>(null)

const phoneObjectSchema = z.object({
  country: z.string().optional().nullable(),
  countryCallingCode: z.string().optional().nullable(),
  countryCode: z.string().optional().nullable(),
  formatted: z.string().optional().nullable(),
  nationalNumber: z.string().optional().nullable(),
  number: z.string().optional().nullable(),
  possible: z.boolean().optional().nullable(),
  valid: z.boolean().optional().nullable(),
})

// 1. Define the full schema (with email)
const fullSchema = z.object({
  fullName: z.string({ required_error: 'Full name is required.' }).min(1, { message: 'Full name is required.' }),
  username: z
    .string({
      required_error: 'Username is required.',
    })
    .min(3, { message: 'Username must be at least 3 characters.' })
    .max(30, { message: 'Username must not exceed 30 characters.' })
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, {
      message: 'Username should only contain letters, numbers, or underscore and should not start with a number.',
    }),
  role: z.object({
    id: z.string({ required_error: 'Role is required.' }),
    name: z.string(),
  }),
  phone: z
    .union([phoneObjectSchema, z.string()])
    .refine(
      (val) =>
        (typeof val === 'object' && val?.number != null && val?.valid === true) ||
        (typeof val === 'string' && val.trim() !== ''),
      {
        message: 'Please enter a valid phone number.',
      },
    )
    .nullable()
    .optional(),
  email: z
    .string({ required_error: 'Email is required.' })
    .min(1, { message: 'Email is required.' })
    .refine(isValidEmailAddress, { message: 'Invalid email address' }),
})

// 2. Define a schema without email
const groupSchema = fullSchema.omit({ email: true })

// 3. Use a computed to select the schema for AutoForm
const autoFormSchema = computed(() => {
  return availableDomains.value.length > 0 ? groupSchema : fullSchema
})

const companyStore = useCompanyStore()
const { companies } = storeToRefs(companyStore)

const currentCompany = computed(() => {
  return companies.value.find((company) => company.id === props.companyId) || null
})

const toUseCompanyPhone = ref()

const fieldConfig = reactive({
  fullName: {
    label: 'Full Name',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Full Name',
    },
  },
  username: {
    label: 'Username',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Username',
    },
  },
  role: {
    label: 'Role',
    component: 'autocomplete',
    inputProps: {
      placeholder: 'Select role',
      class: 'w-full lg:w-2/5',
      options: roles,
    },
  },
  phone: {
    label: 'Phone',
    component: PhoneInput,
    inputProps: {
      class: 'w-full lg:w-2/5',
      autoDefaultCountry: false,
      defaultCountry: 'US',
      showCheckbox: true,
      company: currentCompany.value,
      contact: props.contact,
      modelValue: normalizePhoneNumber(
        props.contact?.phone || props.contact?.primaryPhone || props.contact?.companyPhone || '',
      ),
      listeners: {
        onUpdateUseCompanyPhone: (val: boolean) => {
          toUseCompanyPhone.value = val
        },
        onCompanyPhoneSave: (val: string) => {
          companyStore.updateCompanyPhone(currentCompany.value?.id, val)
        },
      },
    },
  },
  email: {
    label: 'Email',
    inputProps: {
      class: 'w-full lg:w-2/5',
      placeholder: 'Email',
      type: 'email',
    },
  },
})

const roleName = (() => {
  switch (props.contact?.role) {
    case 'client':
      return 'Client'
    case 'client_manager':
    case 'manager':
      return 'Client Manager'
    case 'client_approver':
      return 'Client Approver'
    case 'admin':
      return 'Admin'
  }

  return undefined
})()

// Find the default "Client" role, or use undefined if not found
const clientRoleObject = roles.find((role) => role.id === 'client')
const defaultRole = clientRoleObject || undefined

// Use the full schema for validationSchema in useForm (toTypedSchema)
const form = useForm({
  validationSchema: toTypedSchema(fullSchema),
  initialValues: {
    fullName: props.contact?.name ?? undefined,
    username: props.contact?.username ?? undefined,
    role: props.contact?.role ? { id: props.contact.role, name: roleName } : defaultRole,
    email: props.contact?.email ?? undefined,
  },
})

// Generate username from full name
const hasUserTypedUsername = ref(false)

// Watch fullName to generate username automatically
watch(
  () => form.values.fullName,
  (newFullName) => {
    if (!hasUserTypedUsername.value && newFullName) {
      const generated = generateUsername(newFullName)

      form.setFieldValue('username', generated)
    }
  },
)

// Track when user manually types username
const usernameFieldConfig = computed(() => ({
  ...fieldConfig.username,
  inputProps: {
    ...fieldConfig.username.inputProps,
    onInput: () => {
      hasUserTypedUsername.value = true
    },
  },
}))

const handleInvalidSubmit = ({ values, errors, results }: any) => {
  console.error('[CreateContactForm] Vee-Validate direct validation FAILED (onInvalidSubmit):', { values, errors, results })
  isFormSubmittedOnce.value = true
  isLoadingInternally.value = false
}

onBeforeUnmount(() => {
  if (props.contact) {
    form.setFieldValue('fullName', '')
    form.setFieldValue('email', '')
    form.setFieldValue('phone', undefined)
    form.setFieldValue('role', undefined)
  }
})

// Helper function to parse backend errors and set form fields
function parseAndSetSubmissionErrors(
  error: any,
  formContext: FormContext<Record<string, any>, Record<string, any>>, // Using FormContext type
  generalErrorRef: typeof generalBackendError, // Using typeof for ref type
) {
  let backendErrorMessage = 'An unexpected error occurred. Please try again.'

  if (error && typeof error.message === 'string') {
    backendErrorMessage = error.message
  }

  if (backendErrorMessage.includes('user.invalid_phone') || backendErrorMessage.toLowerCase().includes('phone')) {
    formContext.setFieldError('phone', backendErrorMessage)
  } else if (backendErrorMessage.toLowerCase().includes('email')) {
    formContext.setFieldError('email', backendErrorMessage)
  } else if (backendErrorMessage.toLowerCase().includes('name') || backendErrorMessage.toLowerCase().includes('fullname')) {
    formContext.setFieldError('fullName', backendErrorMessage)
  } else {
    generalErrorRef.value = backendErrorMessage
  }
}

// Helper function for creating or updating user
async function executeUserCreationOrUpdate(
  formValues: Record<string, any>,
  isNewContact: boolean,
  contactToUpdateId: string | undefined,
  currentCompanyId: string | null | undefined,
  emitEvent: (
    event: 'closeDialog',
    payload: { companyIdToBeRefetched: string | null | undefined; updateCount: boolean },
  ) => void,
  generalErrorRef: typeof generalBackendError,
) {
  const phoneToSubmit =
    typeof formValues.phone === 'object'
      ? formValues.phone?.number
        ? formValues.phone?.number
        : ''
      : formValues.phone
        ? formValues.phone
        : ''
  const payload: {
    name: string
    username: string
    role: string
    email: string
    companyPhone?: string
    phone?: string
  } = {
    name: formValues.fullName,
    username: formValues.username,
    role: formValues.role.id,
    email: formValues.email,
  }

  if (toUseCompanyPhone.value) {
    payload.companyPhone = phoneToSubmit
  } else {
    const cleanedPhone = phoneToSubmit.startsWith('+') ? phoneToSubmit.substring(1) : phoneToSubmit

    payload.phone = cleanedPhone
  }

  const handleApiSuccess = async (newlyCreatedUserData?: any) => {
    const newUserId = newlyCreatedUserData?.id

    if (isNewContact && newlyCreatedUserData && typeof newUserId === 'string' && currentCompanyId) {
      await setCompanyMember(currentCompanyId, { id: newUserId })
    }
    await nextTick()
    if (newlyCreatedUserData) {
      emitEvent('closeDialog', { companyIdToBeRefetched: currentCompanyId, updateCount: true })
    } else {
      emitEvent('closeDialog', { companyIdToBeRefetched: currentCompanyId, updateCount: false })
    }
    isLoadingInternally.value = false
  }

  const handleApiError = (error: any, defaultMessage: string) => {
    let errorMessage = defaultMessage

    if (error) {
      // If backend returned an array of messages, use the first one
      if (Array.isArray(error.message)) {
        errorMessage = error.message[0] || defaultMessage
      } else if (typeof error.message === 'string') {
        // Strip off any trailing codes (after the first comma)
        errorMessage = error.message.split(',')[0].trim()
      }
    }
    isLoadingInternally.value = false
    generalErrorRef.value = errorMessage
  }

  if (isNewContact) {
    const { onSuccess, onError } = createContact(payload)

    onSuccess(async (newUserData: any) => handleApiSuccess(newUserData))
    onError((error: any) => handleApiError(error, 'Failed to create contact.'))
  } else {
    if (contactToUpdateId) {
      isLoadingInternally.value = true
      const { onSuccess, onError } = updateContact(contactToUpdateId, payload)

      onSuccess(async () => handleApiSuccess())
      onError((error: any) => handleApiError(error, 'Failed to update contact.'))
    } else {
      generalErrorRef.value = 'Contact ID is missing for update.'
      console.error('Contact ID is missing for update.')
    }
  }
}

const handleFormInternalSubmit = async (values: Record<string, any>) => {
  isFormSubmittedOnce.value = true
  generalBackendError.value = null
  form.setErrors({})
  form.setValues(values, false)
  await nextTick()

  const validationResult = await form.validate()

  if (!validationResult.valid) {
    isLoadingInternally.value = false

    return
  }

  isLoadingInternally.value = true
  try {
    if (props.overrideSubmitHandler) {
      await props.overrideSubmitHandler(
        { ...values, phone: values.phone?.number, username: values.username },
        props.contact?.id,
      )
    } else {
      await executeUserCreationOrUpdate(
        { ...values },
        !props.contact, // isNewContact
        props.contact?.id, // contactToUpdateId
        props.companyId, // currentCompanyId
        (event: any, payload: any) => emit(event, payload),
        generalBackendError,
      )
    }
  } catch (error: any) {
    console.error('Error in submit process (outer catch):', error)
    parseAndSetSubmissionErrors(error, form, generalBackendError)
  } finally {
    if (isLoadingInternally.value) {
      isLoadingInternally.value = false
    }
  }
}

const tellParentToSubmitOrSubmitInternally = () => {
  isFormSubmittedOnce.value = true
  generalBackendError.value = null
}

const errorMessage = computed(() => {
  if (generalBackendError.value) {
    return generalBackendError.value
  }
  if (!isFormSubmittedOnce.value) {
    return ''
  }

  if (form.errors.value && Object.keys(form.errors.value).length > 0) {
    if (form.errors.value.phone) {
      return form.errors.value.phone
    }
    const otherErrors = Object.entries(form.errors.value)
      .filter(([key]) => key !== 'phone')
      .map(([, value]) => value)

    if (otherErrors.some((err) => typeof err === 'string' && err.includes('required'))) {
      return 'Please fill in all the required fields to create the user'
    }
    const firstOtherError = otherErrors.find((err) => typeof err === 'string')

    if (firstOtherError) return firstOtherError
  }

  return ''
})

const resetInternalLoading = () => {
  isLoadingInternally.value = false
  isFormSubmittedOnce.value = false
  generalBackendError.value = null
  form.setErrors({})
}

defineExpose({ resetInternalLoading })

const emailPrefix = ref('')
const emailDomain = ref('')
const availableDomains = computed(() => {
  if (!props.companyId) return []
  const company = companies.value.find((c) => c.id === props.companyId)

  return company?.domains || []
})

// Pre-fill prefix/domain if editing
onMounted(() => {
  if (props.contact?.email) {
    const [prefix, ...domainParts] = props.contact.email.split('@')

    emailPrefix.value = prefix || ''
    emailDomain.value = domainParts.join('@') || ''
  } else if (availableDomains.value.length) {
    emailDomain.value = availableDomains.value[0]
  }
  if (props.contact?.companyPhone) {
    toUseCompanyPhone.value = true
  } else {
    toUseCompanyPhone.value = false
  }
})

// Keep form's email field in sync
watch([emailPrefix, emailDomain], ([prefix, domain]) => {
  if (prefix && domain) {
    form.setFieldValue('email', `${prefix}@${domain}`)
  } else {
    form.setFieldValue('email', '')
  }
})

// Use a filtered fieldConfig (no email key)
const filteredFieldConfig = computed(() => {
  const { email, ...rest } = fieldConfig

  return rest
})

const showGroupEmail = computed(() => availableDomains.value.length > 0)
const fallbackEmail = ref('')

// Watch for fallback email input if no domains
watch(fallbackEmail, (val) => {
  if (!showGroupEmail.value) {
    form.setFieldValue('email', val)
  }
})

// Auto-clean emailPrefix if user pastes a full email
watch(emailPrefix, (val) => {
  if (val && val.includes('@')) {
    const [prefix] = val.split('@')

    emailPrefix.value = prefix
  }
})

// Handle custom validation message for the email prefix input
const handleEmailPrefixInvalid = (e: Event) => {
  const target = e.target as HTMLInputElement

  target.setCustomValidity('Email is required.')
}

const handleEmailPrefixInput = (e: Event) => {
  const target = e.target as HTMLInputElement

  if (target.validationMessage) {
    target.setCustomValidity('')
  }
}
</script>

<template>
  <div class="flex flex-col w-full" :style="{ height: 'calc(100% - 70px)' }">
    <LoadingOverlay
      :active="isLoadingInternally"
      :can-cancel="false"
      loader="dots"
      color="#007bff"
      background-color="rgba(255, 255, 255, 0.5)"
      :is-full-page="false"
    />
    <Alert v-if="errorMessage" variant="destructive" class="!bg-destructive text-white mb-5 !flex items-center">
      <TriangleAlert color="white" class="w-4 h-4" />
      <AlertDescription class="font-semibold">
        {{ errorMessage }}
      </AlertDescription>
    </Alert>

    <div class="flex w-full">
      <AutoForm
        class="w-full space-y-6"
        :schema="autoFormSchema"
        :field-config="
          {
            ...filteredFieldConfig,
            username: usernameFieldConfig,
          } as any
        "
        :form="form"
        @submit="handleFormInternalSubmit"
      >
        <!-- Email Field: Show group email if domains, fallback to regular email input if not -->
        <template v-if="showGroupEmail">
          <FormField name="email" :validate-on-mount="false">
            <FormItem class="space-y-2">
              <Label for="email-prefix">Email address<span class="text-red-500"> *</span> </Label>
              <FormControl>
                <div class="flex items-center gap-2 rounded-md w-full lg:w-2/5">
                  <Input
                    id="email-prefix"
                    v-model="emailPrefix"
                    placeholder="email"
                    class="flex-1 min-w-0 h-10"
                    aria-label="Group email prefix"
                    required
                    @invalid="handleEmailPrefixInvalid"
                    @input="handleEmailPrefixInput"
                  />
                  <span class="text-sm" aria-hidden="true">@</span>
                  <Select v-model="emailDomain" :disabled="!availableDomains.length">
                    <SelectTrigger class="flex-1 min-w-0 h-10" aria-label="Email domain">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem v-for="domain in availableDomains" :key="domain" :value="domain">
                        {{ domain }}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </template>
        <!-- End Email Field -->
        <div class="h-6">
          <div
            class="flex justify-end w-full gap-2 absolute bottom-0 p-4 rounded-b-sm right-0 bg-background-light border-t-2 border-border z-[100]"
          >
            <DialogCheck :show-icon="true" @dialog-should-close="handleDialogShouldClose" />
            <slot v-bind="{ onSubmit: tellParentToSubmitOrSubmitInternally }">
              <Button
                type="submit"
                :disabled="isLoadingInternally || isParentSubmitting"
                @click="form.handleSubmit(handleFormInternalSubmit, handleInvalidSubmit)"
              >
                <Loader2 v-if="isLoadingInternally || isParentSubmitting" class="w-4 h-4 mr-2 animate-spin" />
                <span v-if="contact" class="text-base">Update</span>
                <span v-else class="text-base">Create</span>
              </Button>
            </slot>
          </div>
        </div>
      </AutoForm>
    </div>
  </div>
</template>
